import { useCallback, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import Model from "@tripian/model";
import { api } from "@tripian/core";
import {
  saveNotification,
  changePlans,
  changeReference,
  changeReadOnly,
} from "../redux/action/trip";
import {
  adaptTimelineTripToReference,
  adaptTimelinePlansToPlan,
  groupTimelinePlansByDate,
  groupTimelinePlansBySegment,
  getPlansFromTimelineDays,
  TimelineDay,
  TimelineSegmentGroup,
} from "../utils/timelineAdapters";
import * as ROUTER_PATH_TITLE from "../constants/ROUTER_PATH_TITLE";

// Polling interval in milliseconds
const POLLING_INTERVAL = 3000;
const MAX_POLLING_ATTEMPTS = 60; // Max ~3 minutes of polling

/**
 * Check if ALL available segments are ready (not generating)
 * generatedStatus === 0: still generating → keep polling
 * generatedStatus === 1: ready → show data
 * generatedStatus < 0 (error): stop polling, show current data
 *
 * Returns true if ALL available segments have generatedStatus !== 0
 * Returns false if ANY available segment has generatedStatus === 0
 */
const allAvailableSegmentsReady = (plans: Model.TimelinePlan[]): boolean => {
  if (!plans || plans.length === 0) return false;

  const availablePlans = plans.filter(plan => plan.available);
  if (availablePlans.length === 0) return true; // No available plans, nothing to wait for

  // All available plans must have generatedStatus !== 0
  return availablePlans.every(plan => plan.generatedStatus !== 0);
};

const useTimeline = () => {
  const [loadingTimeline, setLoadingTimeline] = useState<boolean>(false);
  const [loadingTimelineShare, setLoadingTimelineShare] = useState<boolean>(false);
  const [timelineTrip, setTimelineTrip] = useState<Model.TimelineTrip | null>(null);
  const [timelineDays, setTimelineDays] = useState<TimelineDay[]>([]);
  const [timelineSegmentGroups, setTimelineSegmentGroups] = useState<TimelineSegmentGroup[]>([]);
  const [timelineReadOnly, setTimelineReadOnly] = useState<boolean>(false);
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const pollingAttemptsRef = useRef<number>(0);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  /**
   * Stop polling
   */
  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
    setIsPolling(false);
    pollingAttemptsRef.current = 0;
  }, []);

  /**
   * Update state with trip data
   */
  const updateTripState = useCallback(
    (trip: Model.TimelineTrip, readOnly: boolean) => {
      setTimelineTrip(trip);
      setTimelineReadOnly(readOnly);

      // Group plans by date for proper day display (legacy)
      const days = groupTimelinePlansByDate(trip.plans || []);
      setTimelineDays(days);

      // Group plans by segments from tripProfile.segments (new segment-based view)
      const segmentGroups = groupTimelinePlansBySegment(
        trip.plans || [],
        trip.tripProfile?.segments
      );
      setTimelineSegmentGroups(segmentGroups);

      // Update Redux - use adaptTimelinePlansToPlan directly to preserve all steps
      // (groupTimelinePlansByDate filters steps by date which can drop steps after segment edits)
      const adaptedReference = adaptTimelineTripToReference(trip);
      const adaptedPlans = adaptTimelinePlansToPlan(trip.plans || []);
      dispatch(changeReference(adaptedReference));
      dispatch(changePlans(adaptedPlans));
      dispatch(changeReadOnly(readOnly));
    },
    [dispatch]
  );

  /**
   * Fetch timeline trip details with steps
   * Polls until ALL available segments have generatedStatus !== 0
   * Updates UI on each poll so ready segments are shown immediately
   * Main loading stops after first data, background polling continues if needed
   */
  const timelineFetch = useCallback(
    (tripHash: string, minDayIndex = 0, force = false): Promise<Model.TimelineTrip> => {
      setLoadingTimeline(true);
      stopPolling();
      let isFirstFetch = true;

      const fetchAndCheck = (): Promise<Model.TimelineTrip> => {
        // retryCount=0 to disable built-in polling
        return api
          .timeline(tripHash, minDayIndex, force, undefined, 0)
          .then((trip: Model.TimelineTrip) => {
            // Always update state with current data (show ready segments, loading for others)
            updateTripState(trip, false);

            // First fetch - always stop main loading immediately
            if (isFirstFetch) {
              setLoadingTimeline(false);
              isFirstFetch = false;
            }

            // Check if ALL available segments are ready (generatedStatus !== 0)
            if (allAvailableSegmentsReady(trip.plans || [])) {
              // All ready - stop polling
              stopPolling();
              return trip;
            }

            // Some segments still generating (generatedStatus === 0) - check polling attempts
            pollingAttemptsRef.current += 1;
            if (pollingAttemptsRef.current >= MAX_POLLING_ATTEMPTS) {
              // Max attempts reached - stop polling, show what we have
              console.warn("Max polling attempts reached, showing current data");
              stopPolling();
              return trip;
            }

            // Continue polling in background for remaining segments
            setIsPolling(true);
            return new Promise<Model.TimelineTrip>((resolve) => {
              pollingRef.current = setTimeout(() => {
                fetchAndCheck().then(resolve);
              }, POLLING_INTERVAL);
            });
          });
      };

      return fetchAndCheck().catch(timelineFetchError => {
        stopPolling();
        setLoadingTimeline(false);
        dispatch(
          saveNotification(
            Model.NOTIFICATION_TYPE.ERROR,
            "timelineFetch",
            "Get Timeline",
            timelineFetchError
          )
        );
        navigate(ROUTER_PATH_TITLE.TRIPS.PATH);
        throw timelineFetchError;
      });
    },
    [dispatch, navigate, stopPolling, updateTripState]
  );

  /**
   * Fetch shared timeline trip
   * Uses /shared-timeline/{tripHash} endpoint
   * Polls until ALL available segments have generatedStatus !== 0
   * Updates UI on each poll so ready segments are shown immediately
   * Main loading stops after first data, background polling continues if needed
   */
  const timelineGetShared = useCallback(
    (tripHash: string, minDayIndex = 0): Promise<Model.TimelineTrip> => {
      setLoadingTimeline(true);
      stopPolling();
      let isFirstFetch = true;

      const fetchAndCheck = (): Promise<Model.TimelineTrip> => {
        // Use the dedicated shared timeline endpoint
        return api
          .timelineGetShared(tripHash)
          .then((trip: Model.TimelineTrip) => {
            // Always update state with current data (show ready segments, loading for others)
            updateTripState(trip, true);

            // First fetch - always stop main loading immediately
            if (isFirstFetch) {
              setLoadingTimeline(false);
              isFirstFetch = false;
            }

            // Check if ALL available segments are ready (generatedStatus !== 0)
            if (allAvailableSegmentsReady(trip.plans || [])) {
              // All ready - stop polling
              stopPolling();
              return trip;
            }

            // Some segments still generating (generatedStatus === 0) - check polling attempts
            pollingAttemptsRef.current += 1;
            if (pollingAttemptsRef.current >= MAX_POLLING_ATTEMPTS) {
              // Max attempts reached - stop polling, show what we have
              console.warn("Max polling attempts reached, showing current data");
              stopPolling();
              return trip;
            }

            // Continue polling in background for remaining segments
            setIsPolling(true);
            return new Promise<Model.TimelineTrip>((resolve) => {
              pollingRef.current = setTimeout(() => {
                fetchAndCheck().then(resolve);
              }, POLLING_INTERVAL);
            });
          });
      };

      return fetchAndCheck().catch(timelineFetchError => {
        stopPolling();
        setLoadingTimeline(false);
        dispatch(
          saveNotification(
            Model.NOTIFICATION_TYPE.ERROR,
            "timelineGetShared",
            "Get Shared Timeline",
            timelineFetchError
          )
        );
        navigate(ROUTER_PATH_TITLE.TRIPS.PATH);
        throw timelineFetchError;
      });
    },
    [dispatch, navigate, stopPolling, updateTripState]
  );

  /**
   * Share/unshare timeline trip
   */
  const timelineShare = useCallback(
    (tripHash: string, shared: boolean): Promise<boolean> => {
      setLoadingTimelineShare(true);

      return api
        .tripShare(tripHash, shared)
        .then((result: boolean) => {
          if (timelineTrip && timelineTrip.tripHash === tripHash) {
            setTimelineTrip({ ...timelineTrip, shared });
          }
          return result;
        })
        .catch(timelineShareError => {
          dispatch(
            saveNotification(
              Model.NOTIFICATION_TYPE.ERROR,
              "timelineShare",
              "Update Timeline Share",
              timelineShareError
            )
          );
          throw timelineShareError;
        })
        .finally(() => {
          setLoadingTimelineShare(false);
        });
    },
    [dispatch, timelineTrip]
  );

  /**
   * Delete a segment from timeline
   * DELETE /timeline/{tripHash}/{segmentIndex}
   * After successful deletion, refetches the timeline to update state
   */
  const timelineSegmentDelete = useCallback(
    (tripHash: string, segmentIndex: number): Promise<void> => {
      setLoadingTimeline(true);

      return api
        .timelineSegmentDelete(tripHash, segmentIndex)
        .then(() => {
          // After successful deletion, use timelineFetch with polling support
          // Remaining segments may be regenerated (generatedStatus=0) and need polling
          return timelineFetch(tripHash, 0, true).then(() => {});
        })
        .catch(error => {
          setLoadingTimeline(false);
          dispatch(
            saveNotification(
              Model.NOTIFICATION_TYPE.ERROR,
              "timelineSegmentDelete",
              "Delete Segment",
              error
            )
          );
          throw error;
        });
    },
    [dispatch, timelineFetch]
  );

  /**
   * Clear timeline state
   */
  const timelineClear = useCallback(() => {
    setTimelineTrip(null);
    setTimelineDays([]);
    setTimelineSegmentGroups([]);
    setTimelineReadOnly(false);
  }, []);

  /**
   * Update timeline trip state manually
   */
  const updateTimelineTrip = useCallback((trip: Model.TimelineTrip) => {
    setTimelineTrip(trip);
  }, []);

  return {
    loadingTimeline,
    loadingTimelineShare,
    isPolling,
    timelineTrip,
    timelineDays,
    timelineSegmentGroups,
    timelineReadOnly,
    timelineFetch,
    timelineGetShared,
    timelineShare,
    timelineSegmentDelete,
    timelineClear,
    updateTimelineTrip,
    stopPolling,
  };
};

export default useTimeline;
